import React from "react";
import { TabsContent } from "../../components/misc/Tabs";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../components/misc/Card";
import { Label } from "../../components/misc/Label";
import { Input } from "../../components/misc/Input";
import { Limits } from "../types";

interface LimitsTabOverlayProps {
    limits: Limits | null;
    setLimits: React.Dispatch<React.SetStateAction<Limits | null>>;
}

// Mirrors the API's AUTOPILOT_PRO_LISTING_LIMIT — the coverage AI Pro grants.
// Anything higher promises autopilot listings the plan never allocates.
const MAX_AUTOPILOT_LISTING_LIMIT = 12;

// Labels are derived from the field name; this field spells the feature one
// word and the product spells it two.
const LIMIT_LABELS: Partial<Record<keyof Limits, string>> = {
    autopilotListingLimit: "Auto Pilot Listing Limit",
    interactiveAiLimitMicros: "Niche Finder AI Allowance ($/month)",
};

const LimitsTabOverlay: React.FC<LimitsTabOverlayProps> = ({
    limits,
    setLimits,
}) => {
    if (limits === null) {
        return (
            <TabsContent value="userLimits">
                <p>No limits data available.</p>
            </TabsContent>
        );
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const parsed = Number(value);
        if (name === "interactiveAiLimitMicros") {
            // Shown in dollars; stored in micro-dollars, rounded to whole cents (the field steps 0.01).
            setLimits({ ...limits, interactiveAiLimitMicros: Math.max(0, Math.round(parsed * 100) * 10_000) });
            return;
        }
        setLimits({
            ...limits,
            [name]:
                name === "autopilotListingLimit"
                    ? Math.min(parsed, MAX_AUTOPILOT_LISTING_LIMIT)
                    : parsed,
        });
    };

    return (
        <TabsContent value="userLimits" className="w-full flex justify-center">
            <Card className="w-full max-w-2xl">
                <CardHeader>
                    <CardTitle>Edit Limits</CardTitle>
                    <CardDescription>
                        Adjust the warning and limit values for various
                        features.
                    </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-6">
                    {Object.entries(limits)
                        .filter(
                            ([key]) =>
                                ![
                                    "_id",
                                    "created_at",
                                    "updatedAt",
                                    "modified_at",
                                    "__v",
                                    "createdAt",
                                ].includes(key)
                        )
                        .map(([key, value]) => (
                            <div
                                key={key}
                                className="grid grid-cols-2 items-center gap-4"
                            >
                                <Label
                                    htmlFor={key}
                                    className="text-right capitalize"
                                >
                                    {LIMIT_LABELS[key as keyof Limits] ??
                                        key.replace(/([A-Z])/g, " $1").trim()}
                                    :
                                </Label>
                                <div className="w-full">
                                    <Input
                                        type="number"
                                        id={key}
                                        name={key}
                                        value={key === "interactiveAiLimitMicros" ? Number(value) / 1_000_000 : value}
                                        onChange={handleChange}
                                        min={0}
                                        max={
                                            key === "autopilotListingLimit"
                                                ? MAX_AUTOPILOT_LISTING_LIMIT
                                                : undefined
                                        }
                                        step={key === "interactiveAiLimitMicros" ? 0.01 : undefined}
                                        className="w-full"
                                    />
                                    {key === "autopilotListingLimit" && (
                                        <p className="mt-1 text-xs text-gray-500">
                                            Max {MAX_AUTOPILOT_LISTING_LIMIT} —
                                            the AI Pro Auto Pilot coverage cap. 0
                                            disables Auto Pilot.
                                        </p>
                                    )}
                                    {key === "interactiveAiLimitMicros" && (
                                        <p className="mt-1 text-xs text-gray-500">
                                            Dollars of Niche Finder AI per agent per month. 0 turns Niche Finder off; AI Pro is $2.00.
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                </CardContent>
            </Card>
        </TabsContent>
    );
};

export default LimitsTabOverlay;
